import React, { useEffect, useState } from "react";
import "./css/DataCleanUp.css";
import LoadingSpinner from './LoadingSpiner';
import CalculatePoints from "./CalculatePoints";
import NewCSVPopup from "./NewCSVPopup";


function USSADataCleanup(props) {
    const csvData = props.csvData;
    const setCsvData = props.setCsvData;
    const headerInfo = props.headerInfo;
    const setHeaderInfo = props.setHeaderInfo;


    // STATES
    const [isLoading, setIsLoading] = useState(false);
    // popup for showing the changes that will be made to the csv data from the fis DB
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const  originalData = headerInfo.originalData;

    // FUNCTIONS
    const handlePopupClose = () => {
        setIsPopupOpen(false);
        setPopupData(null);
    };

    const checkForMissingUSSARacers = async (data, licenceList) => {
        // data = results from points list API
        // licenceList = all USSA codes from the original CSV/processedData

        const parseXMLString = (xmlString) => {
            let result = {};

            // Splitting XML string based on tags
            const tags = xmlString.split('<');

            for(let i = 0; i < tags.length; i ++){
                for( let j = i; j < tags.length; j++){
                    //  if its not a closing tag
                    if(!tags[j].split('>')[1]?.includes('\n')){
                        if(tags[j].includes('ussa_id>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['USSAcode'] = item[1]
                            }
                        }
                        if(tags[j].includes('last_name>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['lastname'] = item[1]
                            }
                        }
                        if(tags[j].includes('first_name>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['firstname'] = item[1]
                            }
                        }
                        if(tags[j].includes('gender>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['gender'] = tags[j].split('>')[1]
                            }
                        }
                        if(tags[j].includes('year_of_birth>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['year'] = tags[j].split('>')[1]
                            }
                        }
                        if(tags[j].includes('member_current>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['member_current'] = tags[j].split('>')[1]
                            }
                        }
                        if(result['USSAcode'] && result['lastname'] && result['firstname'] && result['gender'] && result['year']){
                            break
                        }
                    }
                }
            }

            return result || null
        }

        // Identify codes present in the points list results
        const codesInPointsList = new Set((data || []).map(racer => racer.USSAcode).filter(Boolean));

        // Identify codes from the original list that are *missing* from the points list
        const codesToLookup = licenceList.filter(code => code && !codesInPointsList.has(code));

        console.log(`Found ${codesInPointsList.size} codes in points list. Need to look up ${codesToLookup.length} missing codes individually.`);
        console.log('The codes are: ', codesToLookup)
        
        let notKnownRacers = [] // Stores *original racer objects* identified as invalid (`member_current=no`)
        let notFoundCodes = new Set(); // Stores codes the lookup API didn't return data for
        let newDataFromLookup = []; // Store successfully looked-up racer data to potentially add back

        // hit the fis api for each fis list untill it returns one
        let apiUrl = 'https://bpw6fhnfw2qknsr3zgiavzh5ly0akhfd.lambda-url.us-west-2.on.aws/'

        // Only proceed if there are actually codes to look up
        if (codesToLookup.length > 0) {
            try {
                // Send a GET request to the API endpoint using fetch
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        },
                    body: JSON.stringify({ USSAcodes: codesToLookup }), // Send ONLY the missing codes
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
            
                // Parse the XML response
                const returnedUSSAData = await response.json()

                codesToLookup.forEach((USSAID) =>{ // Iterate over the codes we *actually* looked up
                    console.log(`[Debug Loop] Processing USSAID: ${USSAID}`);
                    const apiDataForID = returnedUSSAData[USSAID];
                    console.log(`[Debug Loop] Data returned from API for ${USSAID}:`, apiDataForID);
                    // Check if API actually returned data for this ID
                    if (apiDataForID) {
                        let parsedData = parseXMLString(returnedUSSAData[USSAID])
                        if (parsedData && parsedData.USSAcode) { // Check if parsing was successful AND found a code
                            // Found and parsed data for this missing USSAID
                            if(parsedData['member_current'] === 'no'){
                                console.log(`USSAID ${USSAID} lookup returned member_current=no`);
                                // We'll add the *code* to the `invalidUSSARacers` list for now - needs refinement.
                                // Ideally, this function should return codes identified as invalid, not racer objects.
                                notKnownRacers.push(USSAID); // Temporarily store the CODE
                            } else {
                                // Valid racer found via lookup, store their data
                                let personInfo = { ...parsedData }; // Adapt parsed data structure if needed
                                // Make sure required fields match the main data structure
                                personInfo['USSAcode'] = USSAID; // Ensure code is present
                                personInfo['first_name'] = parsedData['firstname'];
                                personInfo['last_name'] = parsedData['lastname'];
                                personInfo['gender'] = parsedData['gender'];
                                personInfo['nation'] = parsedData['nationcode']; // Assuming nationcode exists
                                personInfo['date_of_birth'] = parsedData['year']; // Assuming year is DOB
                                // Add points? This API doesn't return points.
                                newDataFromLookup.push(personInfo);
                            }
                        } else {
                            // Parsing failed OR didn't yield expected data (likely an API error message)
                            console.warn(`Failed to parse USSA data for USSAID: ${USSAID}. Marking as not found.`);
                            notFoundCodes.add(USSAID);
                        }
                    } else {
                        // API did not return any data for this USSAID
                        console.log(`[Debug Loop] Entering ELSE block for ${USSAID} (API returned no data).`);
                        console.log(`USSAID ${USSAID} lookup returned no data.`);
                        notFoundCodes.add(USSAID);
                        console.log(`[Debug Loop] Added ${USSAID} to notFoundCodes. Current Set:`, notFoundCodes);
                    }
                })
                
            
            } catch (error) {
                console.error('Error fetching data for USSA racers from USSA information lambda', error.message);
                // If the API call fails entirely, mark all looked-up codes as not found?
                codesToLookup.forEach(code => notFoundCodes.add(code));
            }
        } // End if (codesToLookup.length > 0)

        // Combine original points list data with newly found valid racers
        const combinedData = [...data, ...newDataFromLookup];

        // Return combined data, codes identified as invalid, and codes not found
        return [combinedData, notKnownRacers, Array.from(notFoundCodes)]
    }

    const checkForMissingFISRacers = async (data, licenceList) => {
        /**
         * go through the data (list of racers returned by points list) and check 
         * if its missing any from the licenceList. if it is then hit the FIS API 
         * for those racers to get information on them. then reurn the list of new 
         * data and the list of the unknown racers.
         */

        const parseXMLString = (xmlString) => {
            let result = {};

            // Splitting XML string based on tags
            const tags = xmlString.split('<');

            for(let i = 0; i < tags.length; i ++){
                for( let j = i; j < tags.length; j++){
                    //  if its not a closing tag
                    if(!tags[j].split('>')[1]?.includes('\n')){
                        if(tags[j].includes('fiscode>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['fiscode'] = item[1]
                            }
                        }
                        if(tags[j].includes('lastname>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['lastname'] = item[1]
                            }
                        }
                        if(tags[j].includes('firstname>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['firstname'] = item[1]
                            }
                        }
                        if(tags[j].includes('gender>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['gender'] = item[1]
                            }
                        }
                        if(tags[j].includes('nationcode>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['nationcode'] = item[1]
                            }
                        }
                        if(tags[j].includes('year>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['year'] = item[1]
                            }
                        }
                        if(tags[j].includes('status>')){
                            let item = tags[j].split('>')
                            if(!item[0].includes('/')){
                                result['status'] = item[1]
                            }
                        }
                        if(result['fiscode'] && result['lastname'] && result['firstname'] && result['gender'] && result['nationcode'] && result['year']){
                            break
                        }
                    }
                }
            }

            return result || null
        }

        // check the new data for racers that got nothing returned for them. in that case then hit the api for them and add thei names, country and yob
        let FIScodes = [];
        (data || []).forEach((racer) => {
            FIScodes.push(racer['fis_licence'])
        })

        console.log('getting information of FIS racers:', FIScodes)

        let notKnownRacers = []

        // hit the fis api for each fis list untill it returns one
        let apiUrl = 'https://p474fmngovil3jmfzxh75fuvg40ksjuf.lambda-url.us-west-2.on.aws/'

        try {
            // Send a GET request to the API endpoint using fetch
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ FIScodes }),
            });         

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
        
            // Parse the XML response
            const returnedFISData = await response.json()
            FIScodes.forEach((FISID) => {
                if(!returnedFISData[FISID]){
                    console.log('NO FIS XML FOR:', FISID)
                    console.log('NO FIS XML FOR:', returnedFISData[FISID])
                }
                let parsedData = parseXMLString(returnedFISData[FISID])
                if(parsedData){
                    if(parsedData['status'] === 'E'){
                        data.forEach((racer) => {
                            if(racer['FIScode'] === FISID){
                                notKnownRacers.push(racer)
                            }
                        })
                    }
                    // this is the person to add
                    let personInfo = {}
                    personInfo['first_name'] = parsedData['firstname']
                    personInfo['last_name'] = parsedData['lastname']
                    personInfo['gender'] = parsedData['gender']
                    personInfo['nation'] = parsedData['nationcode']
                    // personInfo['fis_licence'] = parsedData['fiscode']
                    personInfo['date_of_birth'] = parsedData['year']
                    // personInfo['fis_points'] = {'DI': null, 'SP': null}
                    data.push(personInfo)
                }
            })

        
            } catch (error) {
                console.error('Error fetching data for FIS racers from FIS information lambda', error.message);
            }
        return [data, notKnownRacers]
    }

    const cleanData = async () => {
        setIsLoading(true);
        console.log('csv data at the start:', csvData)

        // --- Step 1: Handle FIS Data (Lookup and Identify Invalid FIS) ---
        const hasFISCodes = csvData.some(row => row["FIScode"]);
        let fisApiData = []; // Data combined from FIS points + website lookup
        let invalidFISCodes = []; // List of FIS codes found to be invalid (expired/not found)

        if (hasFISCodes) {
            try {
                const fisPointsResult = await getFISDataPointsList(); // Renamed for clarity
                // Correctly destructure the result: [racerDataArray, notKnownRacersArray]
                const [resolvedFisApiData, /* ignoredNotKnownRacers */] = await checkForMissingFISRacers(fisPointsResult, csvData.map(r => r.FIScode).filter(Boolean));
                fisApiData = resolvedFisApiData; // Assign only the racer data array

                // Identify invalid FIS codes based on status 'E' ONLY
                // Note: Missing from points list does NOT mean the FIS code is invalid
                // It just means they don't have FIS points yet
                invalidFISCodes = csvData
                    .map(r => r.FIScode)
                    .filter(Boolean) // Filter out empty/null codes first
                    .filter(code => {
                        const apiMatch = fisApiData.find(apiR => apiR.fis_licence === code);
                        // Only invalid if status is explicitly 'E' (expired/inactive)
                        return apiMatch && apiMatch.status === 'E';
                    });
                 console.log("Identified Invalid FIS Codes (status E only):", invalidFISCodes);
            } catch (error) {
                console.error("Error during FIS data fetch/processing:", error);
                // Decide how to proceed - maybe stop, maybe continue without FIS updates?
                // For now, we'll continue but FIS data might be incomplete/incorrect
                fisApiData = []; // Ensure it's an array
                invalidFISCodes = [];
            }
        }

        // --- Step 2: Create Initial Processed Data (apply FIS updates, clear invalid FIS codes) ---
        let processedData = csvData.map(row => {
            let modifiedRow = { ...row };
            const apiFisMatch = fisApiData.find(apiR => apiR.fis_licence === modifiedRow.FIScode);

            if (apiFisMatch) {
                 // Apply FIS updates (names, gender, points, nation, YOB)
                 checkNames(apiFisMatch, modifiedRow);
                 addGender(apiFisMatch, modifiedRow);
                 if (modifiedRow["FISpoints"]) {
                     checkFISPoints(apiFisMatch, modifiedRow);
                 } else {
                     addFISPoints(apiFisMatch, modifiedRow); // Note: add_fis_points counter removed, handled differently now
                 }
                 checkNationality(apiFisMatch, modifiedRow);
                 checkYOB(apiFisMatch, modifiedRow);
            }

            // Clear FIS code if it's invalid
            if (modifiedRow.FIScode && invalidFISCodes.includes(modifiedRow.FIScode)) {
                console.log(`Clearing invalid FIS Code: ${modifiedRow.FIScode} for ${modifiedRow.lastname}`);
                modifiedRow.FIScode = ''; // Clear the invalid code
            }
            return modifiedRow;
        });


        // --- Step 3: Handle USSA Data (Lookup, Apply Updates, Identify Invalid USSA) ---
        let ussaApiData = [];
        let invalidUSSARacers = []; // Racers identified as invalid by USSA check -> NOW CONTAINS CODES
        let notFoundUSSACodes = new Set(); // Define BEFORE try block, initialize empty
        let invalidUSSACodeSet = new Set(); // Define BEFORE try block, initialize empty

        try {
            const ussaPointsResult = await getUSSADataPointsList(processedData); // Pass current data
            // Capture all three return values
            const [checkedUssaApiData, identifiedInvalidUssa, identifiedNotFoundUssa] = await checkForMissingUSSARacers(ussaPointsResult, processedData.map(r => r.USSAcode).filter(Boolean));

            ussaApiData = checkedUssaApiData;
            invalidUSSARacers = identifiedInvalidUssa; // These are racers with member_current === 'no' -> NOW CONTAINS CODES
            notFoundUSSACodes = new Set(identifiedNotFoundUssa); // Assign the actual Set inside try

            // Convert the list of invalid codes into a Set for filtering
            invalidUSSACodeSet = new Set(invalidUSSARacers); // Assign the actual Set inside try

            console.log("Identified Invalid USSA Codes (member_current=no):", invalidUSSACodeSet);
            console.log("Identified Not Found USSA Codes:", notFoundUSSACodes);

            // Apply USSA updates
            processedData = changeCSVDataForUSSA(ussaApiData, processedData);

        } catch (error) {
            console.error("Error during USSA data fetch/processing:", error);
             // Decide how to proceed
             ussaApiData = [];
             invalidUSSARacers = [];
             notFoundUSSACodes = new Set(); // Ensure it's defined even on error
             invalidUSSACodeSet = new Set(); // Ensure it's defined even on error
        }

        // --- Step 4: Filter out Racers with Invalid USSA Licenses ---
        // The sets invalidUSSACodeSet and notFoundUSSACodes are already defined above
        console.log("Using Invalid Codes Set:", invalidUSSACodeSet);
        console.log("Using Not Found Codes Set:", notFoundUSSACodes);

        // Special case: If there's a CODEX (FIS race), generate synthetic USSA codes for racers with FIS but no USSA
        const hasCodex = !!headerInfo.codex;
        let syntheticUSSACounter = 1;
        
        if (hasCodex) {
            console.log("CODEX detected - will generate synthetic USSA codes for FIS-only racers");
            processedData.forEach(racer => {
                const hasUSSACode = !!racer.USSAcode;
                const hasFISCode = !!racer.FIScode;
                
                // If no USSA code but has FIS code, generate a synthetic USSA code
                if (!hasUSSACode && hasFISCode) {
                    const syntheticCode = 'M' + String(syntheticUSSACounter).padStart(7, '0');
                    console.log(`Generating synthetic USSA code ${syntheticCode} for FIS racer ${racer.FIScode} (${racer.lastname})`);
                    racer.USSAcode = syntheticCode;
                    syntheticUSSACounter++;
                }
            });
        }

        let removedRacersInfo = []; // Store details of removed racers

        const finalValidData = processedData.filter(racer => {
            // Requirement: Remove if USSA code is missing OR if the existing code is invalid OR if the code was not found by API.
            const hasUSSACode = !!racer.USSAcode; // Check if USSA code exists and is not empty string
            const isInvalidCode = hasUSSACode && invalidUSSACodeSet.has(racer.USSAcode); // Check if the existing code is in the invalid set (`member_current === 'no'`)
            const isNotFoundCode = hasUSSACode && notFoundUSSACodes.has(racer.USSAcode); // Check if the code was simply not found
            
            // Synthetic codes (M followed by 7 digits) are always valid - don't check against API results
            const isSyntheticCode = hasUSSACode && /^M\d{7}$/.test(racer.USSAcode);

            const shouldKeep = hasUSSACode && (isSyntheticCode || (!isInvalidCode && !isNotFoundCode)); // Keep if valid or synthetic

            if (!shouldKeep) {
                // Log details for removed racers
                let reason = !hasUSSACode ? "MISSING" : (isInvalidCode ? "INVALID (member_current=no)" : (isNotFoundCode ? "NOT FOUND BY API" : "UNKNOWN"));
                console.log(`Removing racer: USSAcode='${racer.USSAcode || ''}', Reason=${reason}, Name=${racer.lastname}`);
                removedRacersInfo.push({ racer: { ...racer }, reason: reason }); // Store copy of racer data and reason
            }
            return shouldKeep;
        });

        console.log('Data after USSA validity filter:', finalValidData);


        // --- Step 5: Re-Rank the Final Valid Data ---
        // Note: Removed racersToFlagRed, as filtering is done directly now.
        const rerankedData = reRankRacers(finalValidData); // Pass only the valid data

        console.log('new results after re-ranking:', rerankedData);

        // --- Step 6: Update Headers (Simplified) ---
        // Check if any columns were added implicitly by the update functions
        // This part might need adjustment if add_nation/YOB/points logic was complex
        let headers = [...headerInfo.headers]; // Start with existing headers
        const hasCountry = rerankedData.some(r => r.Country);
        const hasYOB = rerankedData.some(r => r.YOB);
        const hasFISpoints = rerankedData.some(r => r.FISpoints !== undefined && r.FISpoints !== null);

        if (hasCountry && !headers.includes('Country')) headers.push('Country');
        if (hasYOB && !headers.includes('YOB')) headers.push('YOB');
        if (hasFISpoints && !headers.includes('FISpoints')) headers.push('FISpoints');

        if (headers.length !== headerInfo.headers.length) {
             setHeaderInfo((prevState) => ({ ...prevState, "headers": headers }));
        }


        setIsLoading(false);
        setPopupData({ validRacers: rerankedData, removedRacers: removedRacersInfo });
        setIsPopupOpen(true);
    };

    // Helper function calls (getFISDataPointsList, getUSSADataPointsList) would replace parts of getFISData/getUSSAData
    // You'd need to refactor getFISData and getUSSAData slightly
    // to separate the points list API call from the checking/updating logic.

    // Example refactor stubs (implement based on original getFISData/getUSSAData logic)
    const getFISDataPointsList = () => {
         return new Promise((resolve, reject) => {
             // Check if a FIS list is selected
             const fisList = headerInfo.usedFISList;
             if (!fisList || fisList === '' || fisList === 0) { // Assuming 0 or empty string means no selection
                 window.alert("Please select a valid FIS points list before checking FIS data.");
                 setIsLoading(false); // Ensure loading state is reset
                 reject(new Error("No FIS points list selected."));
                 return; // Stop execution
             }

             var racers = csvData.map(row => row["FIScode"]).filter(Boolean);
             var points_list = parseInt(headerInfo['usedFISList']);
             const data_to_send = new FormData();
             data_to_send.append('points_list', points_list);
             data_to_send.append('racers', racers);
             data_to_send.append('return_csv', false);

             fetch("https://ggpwjy2pju3cttzzmoxhnuidyi0bkuwd.lambda-url.us-west-2.on.aws", {
             method: 'POST',
             body: data_to_send
             })
             .then(response => response.json())
             .then(data => {
                 if (data["message"] === undefined) {
                     resolve(data);
                 } else {
                     window.alert(data["message"]);
                     reject(new Error(data["message"]));
                 }
             })
             .catch(error => {
                 console.error("Error fetching FIS points list:", error);
                 reject(error);
             });
         });
    };

     const getUSSADataPointsList = (currentProcessedData) => {
         return new Promise((resolve, reject) => {
            // Check if a USSA/NRL list is selected
            const ussaList = headerInfo.usedNRLList;
            if (!ussaList || ussaList === '' || ussaList === 0) { // Assuming 0 or empty string means no selection
                window.alert("Please select a valid USSA/NRL points list before checking USSA data.");
                setIsLoading(false); // Ensure loading state is reset
                reject(new Error("No USSA/NRL points list selected."));
                return; // Stop execution
            }

            var racers = currentProcessedData.map(row => row["USSAcode"]).filter(Boolean);
             var points_list = parseInt(headerInfo['usedNRLList']);
             const data_to_send = new FormData();
             data_to_send.append('points_list', points_list);
             data_to_send.append('racers', racers);
             data_to_send.append('return_csv', false);
             data_to_send.append('date', headerInfo.raceDate.split("-"));

             fetch("https://2ibtetclowg2xpmh47qlpd7egy0tneqv.lambda-url.us-west-2.on.aws/", {
                method: 'POST',
                body: data_to_send
             })
                .then(response => response.json())
                .then(data => {
                     if (data["message"] === undefined) {
                         resolve(data);
                    } else {
                         window.alert(data["message"]);
                         reject(new Error(data["message"]));
                     }
                 })
                 .catch(error => {
                     console.error("Error fetching USSA points list:", error);
                      // Display a generic error to the user, but the specific missing list error is handled above
                      window.alert("Something seems to be wrong with your USSA Codes or the points list.");
                     reject(error);
                 });
         });
    };


    // The reRankRacers function needs to be updated slightly to accept only one argument
    const reRankRacers = (data) => {
        // Skip re-ranking for sprint finals - rankings are by Highest Round, already calculated
        if (headerInfo.format === 'sprintFinal') {
            return [...data];
        }

        // remove the racers from the results (No longer needed here, filtering done above)
        let updatedCsvData = [...data]; // Work on a copy

        console.log('Data passed to reRankRacers:', updatedCsvData)

        // now update the ranking for the updatedCSVData based on the time. if they have the same time then give them the same rank
        let position = 0;
        let lastTime = null;
        let lastRank = 0;

        // Sort by time first to handle ranking correctly
        updatedCsvData.sort((a, b) => {
             // Handle potential non-numeric times or missing times if necessary
             const timeA = parseFloat(a.time?.replace(':', '.')) || Infinity;
             const timeB = parseFloat(b.time?.replace(':', '.')) || Infinity;
             return timeA - timeB;
        });

        updatedCsvData.forEach((racer) => {
            if(racer['status'] === '' || racer['status'] === 'QLF'){
                position++;
                const currentTime = racer["time"];
                if(currentTime !== lastTime){
                    racer["rank"] = position; // New time = rank equals position
                } else {
                    racer["rank"] = lastRank; // Tie = keep same rank
                }
                lastRank = racer["rank"];
                lastTime = currentTime;
            } else {
                 racer["rank"] = ''; // Assign empty rank for non-finishers
                 lastTime = null; // Reset for next group of finishers
            }
        });

        return updatedCsvData;
    }


    // changeCSVDataForUSSA remains largely the same, applies updates
    const changeCSVDataForUSSA = (apiData, currentProcessedData) => {
        /* ... function implementation ... */
        // Ensure it returns the modified currentProcessedData
        currentProcessedData.forEach((row) => {
            const apiMatch = apiData.find(apiRow => apiRow.USSAcode === row.USSAcode);
            if (apiMatch) {
                addUSSAPoints(apiMatch, row); // Assumes addUSSAPoints is defined
                if (!row.FIScode) { // Only update other details if no FIS code was present/valid
                    checkRestOfRacerInfo(apiMatch, row); // Assumes checkRestOfRacerInfo is defined
                }
            }
        });
        return currentProcessedData;
    };

    // Define addUSSAPoints and checkRestOfRacerInfo if they aren't already
    const addUSSAPoints = (apiRow, row) => {
        // ... implementation ...
         row["NRLpoints"] = parseFloat(apiRow["points"]?.[headerInfo["discipline"]] || 990)
    }

    const checkRestOfRacerInfo = (apiRow, row) => {
        // ... implementation ...
         row['firstname'] = apiRow['first_name']
         row['lastname'] = apiRow['last_name']
         // Ensure date_of_birth exists and has expected format if needed
         row['YOB'] = apiRow['date_of_birth']?.split('-')[0] || row['YOB']; // Get year or keep existing
         row['Country'] = 'USA' // Assuming USSA means USA
    }

     // Define check functions (checkNames, addGender, etc.) if they aren't globally defined
     // ... (definitions as provided before) ...
    const checkNames = (apiRow, row) => {
        // if they are the same then set the first name and last name to the values from the api
        if(row["firstname"] !== apiRow["first_name"]){
            row["firstname"] = apiRow["first_name"]
        };

        if(row["lastname"] !== apiRow["last_name"]){
            row["lastname"] = apiRow["last_name"]
        };
    }

    const checkFISPoints = (apiRow, row) => {
        // if they are the same then set the FIS Points to the values from the api
        // Ensure fis_points and the discipline exist before accessing
        const apiPoints = apiRow?.fis_points?.[headerInfo?.discipline];
        if (apiPoints !== undefined && row["FISpoints"] !== apiPoints) {
            row["FISpoints"] = apiPoints;
        }
    }

    const addFISPoints = (apiRow, row) => {
        // if they are the same then set the FIS Points to the values from the api
        let points = null
        try{
            // Ensure fis_points and the discipline exist before accessing
            const apiPointsValue = apiRow?.fis_points?.[headerInfo?.discipline];
            if (apiPointsValue !== undefined && apiPointsValue !== null) {
                 points = parseFloat(apiPointsValue)
            }
        } catch(err) {
            console.warn("Error parsing FIS points:", err);
            points = null // Keep null on error
        }
        row["FISpoints"] = points;
        // Removed return 1; as it wasn't used effectively for header updates
    }

    const checkNationality = (apiRow, row) => {
        if(apiRow?.nation && row["Country"] !== apiRow['nation']){
            row["Country"] = apiRow["nation"]
            // Removed return 1; - header updates handled differently now
        }
    }

    const checkYOB = (apiRow, row) => {
        if(apiRow?.date_of_birth && row["YOB"] !== apiRow['date_of_birth'].split("-")[0]){
            row["YOB"] = apiRow['date_of_birth'].split("-")[0];
            // Removed return 1; - header updates handled differently now
        }
    }

    const addGender = (apiRow, row) => {
        if (apiRow?.gender) {
             row['sex'] = apiRow['gender'];
        }
    }

    // calculate the top 5 racers with the lowest fis points. this is hard coded and needs to change depending on the fild of the csv.
    const calculateTop5Racers = () => {
        const sortedPeople = [...csvData].sort(
            (a, b) => (a["FISpoints"] || a["USSApoints"]) - (b["FISpoints"] || [b['USSApoints']])
        );
        
        const filteredRacers = sortedPeople.filter(
            racer => racer.rank >= 1 && racer.rank <= 5
        );
        
        const sortedFilteredRacers = [...filteredRacers].sort(
            (a, b) => a.rank - b.rank
        );
        // set the header info wutht he top 5 racers
        setHeaderInfo((prevState) => ({
            ...prevState,
            "top5Racers":sortedFilteredRacers
            }));
    } 

    // make the popup visable or not
    const ToggleCalculatePenalty = () => {
        // create popup for calculating penalty
        const overlay = document.getElementById('overlay');
        if (overlay.style.display === 'flex') {
            overlay.style.display = 'none';
        } else {
            overlay.style.display = 'flex';
            calculateTop5Racers();
        }
    }

    useEffect(() => {
        console.log('original data changed:', originalData)
    }, [originalData])

  return (
    <>
      <div id="dataCleanUp">
        <div id="dataCleanUpBox">
          <div id="instr">
            Once you have imput all the correct info for the Header Section. you
            can then calculate the USSA points for the race if they do not have
            any. First you can check the data agenst the USSA/FIS database. To do so
            use the button below. then you can calculate the FIS points. 
          </div>
            <h4>
                NOTE: This may take a while depending on how many racers you have in the race file!
            </h4>
          <button id="checkData" onClick={cleanData} disabled={isLoading || !csvData || !(csvData.length > 0)}>
            {isLoading ? <LoadingSpinner /> : "Check Data against USSA Data Base"}
          </button>
          <button id="calcAndTog" onClick={ToggleCalculatePenalty} disabled={!csvData || !(csvData.length > 0) || headerInfo.format === 'sprintFinal'}>
            Calculate Penalty / Race Points
          </button>
          
        </div>

        <div id="calculate-points">
            <CalculatePoints 
                headerInfo={headerInfo} 
                setHeaderInfo={setHeaderInfo} 
                csvData={csvData} 
                setCsvData={setCsvData} 
                ToggleCalculatePenalty={ToggleCalculatePenalty}
                parentElement={'ussa'}
            />
        </div>
      </div>

      <NewCSVPopup
        isPopupOpen={isPopupOpen}
        closeWindow={handlePopupClose}
        newData={popupData}
        oldData={originalData}
        headerInfo={headerInfo}
        headers={headerInfo.headers}
        setCsvData={setCsvData}
        callerContext="USSA"
      />

    </>
  );
}

export default USSADataCleanup